<template>
  <div
    class="fixed top-0 left-0 w-full h-full z-[12345678900] flex items-center justify-center bg-gray-900/50 backdrop-blur-sm p-4"
    @click.self="handleCancel"
  >
    <div
      class="w-full max-w-lg overflow-hidden rounded-2xl bg-white shadow-2xl transition-all"
    >
      <div
        class="flex items-center justify-between border-b border-gray-100 px-4 py-3"
      >
        <div class="flex items-center gap-3">
          <div
            class="flex h-10 w-10 items-center justify-center rounded-lg bg-blue-50 text-blue-600"
          >
            <svg
              class="h-6 w-6"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
              />
            </svg>
          </div>
          <h2 class="text-xl font-semibold text-gray-800">Import Players</h2>
        </div>
        <button
          @click="handleCancel"
          class="rounded-full p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600 transition-colors"
        >
          <svg
            class="h-6 w-6"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M6 18L18 6M6 6l12 12"
            />
          </svg>
        </button>
      </div>

      <div class="p-4">
        <div v-if="!isUploadingNewPlayerFromCSV">
          <div class="mb-4">
            <h3
              class="text-sm font-medium text-gray-700 uppercase tracking-wider"
            >
              CSV File
            </h3>
            <p class="text-sm text-gray-500">
              Upload a file containing Player ID in the first column.
            </p>
          </div>

          <div
            @dragover.prevent="handleDragOver"
            @dragleave.prevent="handleDragLeave"
            @drop.prevent="handleDrop"
            @click="$refs.fileInput.click()"
            :class="[
              'relative flex flex-col items-center justify-center rounded-xl border-2 border-dashed p-10 transition-all cursor-pointer',
              isDragging
                ? 'border-blue-400 bg-blue-50'
                : 'border-gray-200 hover:border-blue-300 hover:bg-gray-50',
              formData.csvFile ? 'border-green-300 bg-green-50/30' : '',
            ]"
          >
            <input
              ref="fileInput"
              type="file"
              accept=".csv"
              class="hidden"
              @change="handleFileUpload"
            />

            <div
              :class="[
                'mb-4 rounded-full p-3',
                formData.csvFile
                  ? 'bg-green-100 text-green-600'
                  : 'bg-gray-100 text-gray-400',
              ]"
            >
              <svg
                v-if="!formData.csvFile"
                class="h-8 w-8"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
                />
              </svg>
              <svg
                v-else
                class="h-8 w-8"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M5 13l4 4L19 7"
                />
              </svg>
            </div>

            <div class="text-center">
              <p v-if="formData.csvFile" class="font-medium text-gray-800">
                {{ formData.csvFile.name }}
              </p>
              <p v-else class="text-sm text-gray-600">
                <span class="font-semibold text-blue-600 underline"
                  >Click to upload</span
                >
                or drag and drop
              </p>
              <p class="mt-1 text-xs text-gray-400">CSV format only</p>
            </div>

            <button
              v-if="formData.csvFile"
              @click.stop="removeFile"
              class="mt-4 flex items-center gap-1 text-xs font-medium text-red-500 hover:text-red-700 transition-colors"
            >
              <svg
                class="h-4 w-4"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor text-current"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                />
              </svg>
              Remove file
            </button>
          </div>
        </div>

        <div v-else class="flex flex-col items-center justify-center py-12">
          <div
            class="h-12 w-12 animate-spin rounded-full border-4 border-blue-100 border-t-blue-600"
          ></div>
          <p class="mt-4 font-medium text-gray-600">Importing CSV file...</p>
        </div>
      </div>

      <div
        v-if="!isUploadingNewPlayerFromCSV"
        class="flex items-center justify-end gap-3 border-t border-gray-100 bg-gray-50/50 px-6 py-4"
      >
        <button
          @click="handleCancel"
          class="rounded-lg px-4 py-2 text-sm font-medium text-gray-600 hover:bg-gray-200 transition-colors"
        >
          Cancel
        </button>
        <button
          @click="handleCreate"
          :disabled="!formData.csvFile"
          :class="[
            'flex items-center gap-2 rounded-lg px-6 py-2 text-sm font-semibold text-white shadow-sm transition-all',
            formData.csvFile
              ? 'bg-blue-600 hover:bg-blue-700 shadow-blue-200'
              : 'bg-gray-300 cursor-not-allowed',
          ]"
        >
          <svg
            class="h-4 w-4"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12"
            />
          </svg>
          Import CSV
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import Swal from "sweetalert2";
import { postFormDataFile } from "@/api/axiosClient";

export default {
  name: "FreebetPlayerCSVImport",
  props: {
    detail: { type: Object },
    resources: { type: String, default: "freebets" },
  },
  data() {
    return {
      formData: { csvFile: null },
      isUploadingNewPlayerFromCSV: false,
      isDragging: false,
    };
  },
  computed: {
    ...mapGetters(["isMenuOpen"]),
  },
  methods: {
    resetForm() {
      this.formData.csvFile = null;
      if (this.$refs.fileInput) this.$refs.fileInput.value = "";
    },
    removeFile() {
      this.formData.csvFile = null;
      if (this.$refs.fileInput) this.$refs.fileInput.value = "";
    },
    handleDragOver() {
      this.isDragging = true;
    },
    handleDragLeave() {
      this.isDragging = false;
    },
    handleDrop(event) {
      this.isDragging = false;
      const file = event.dataTransfer.files[0];
      this.validateAndSetFile(file);
    },
    validateAndSetFile(file) {
      if (file && !file.name.endsWith(".csv")) {
        Swal.fire({
          title: "Invalid Format",
          text: "Please select a valid CSV file.",
          icon: "error",
          confirmButtonColor: "#2563eb",
        });
        return;
      }
      this.formData.csvFile = file;
    },
    handleFileUpload(event) {
      const file = event.target.files[0];
      this.validateAndSetFile(file);
    },
    async handleCreate() {
      if (!this.formData.csvFile) return;

      this.isUploadingNewPlayerFromCSV = true;
      try {
        const formData = new FormData();
        formData.append("csvFile", this.formData.csvFile);
        console.log(this.detail);
        let resource = this.resources;
        let paramid = resource === 'campaigns' ? this.detail._id : this.detail?.external_id;
        await postFormDataFile(
          `${this.resources}/importcsv/${paramid}`,
          formData,
        );

        Swal.fire({
          title: "Success",
          text: "CSV file imported successfully!",
          icon: "success",
          confirmButtonColor: "#2563eb",
        }).then(() => {
          window.location.reload();
        });
      } catch (error) {
        console.error("Import error:", error);
        Swal.fire({
          title: "Error",
          text: "Failed to import CSV file.",
          icon: "error",
          confirmButtonColor: "#2563eb",
        });
      } finally {
        this.isUploadingNewPlayerFromCSV = false;
      }
    },
    handleCancel() {
      this.$emit("import-player-modal-open");
      this.resetForm();
    },
  },
};
</script>
